/*
 *     Copyright (c) 2020 Bcame S.r.l.. All rights reserved.
 *     Bcame S.r.l. Technology Business Unit
 *
 *     Company confidential.
 *
 *     This software is proprietary  to and embodies the confidential
 *     technology of Bcame S.r.l..  Possession, use, or copying of this
 *     software and media is authorized only pursuant to a valid written
 *     license from Bcame S.r.l.  or an authorized sublicensor.
 *
 *     Bcame S.r.l.
 */

<template>
  <div class="price-manager">
    <absolute-loader :show-loader="showLoader" mode="fixed" />
    <div class="add-price">
      <price-input
      name="price-input"
      v-model="discountValue"
      v-validate="`required|decimal|min_value:0|max_value:${maxPriceForInput}`"
      :discountType.sync="discountType"
      :parentErrors="errors"
      />
      <tabs-manager v-model="levelTypeId" name="tabs-manager" :levelType.sync="levelType" v-validate="'required'" @activeTile="setActiveTile" />
      <div class="wrapp-save text-right">
        <button type="button" class="btn btn-primary btn-primary-small btn-primary-black btn-rounded btn-primary-with-icon" @click.prevent="addPrice" :disabled="!canAddMore">
          <span class="text">{{$t('Save')}}</span>
          <svg class="o-icon"><use xlink:href="#arrow-right-short"></use></svg>
        </button>
        <p class="t-normal text-center mt-2 cannot-add" v-show="!canAddMore">{{$t('price-adj.messages.cannot-close-order-for-price-reason', { price: totalAmountWithCurrency })}}</p>
      </div>
    </div>
    <active-prices @remove-price="removePrice" />
  </div>
</template>

<script>
import AbsoluteLoader from 'theme/components/AbsoluteLoader/AbsoluteLoader'
import PriceInput from 'theme/components/Checkout/PriceAdjustements/PriceInput'
import TabsManager from 'theme/components/Checkout/PriceAdjustements/Tabs/TabsManager'
import ActivePrices from 'theme/components/Checkout/PriceAdjustements/ActivePrices/ActivePrices.vue'
import { getDataByIdentifier } from 'theme/components/Checkout/PriceAdjustements/Helpers/index.js'
import { randomInRange } from '@/helpers'
import { toast } from 'theme/modules/notifications'

const inputValuesLegend = {
  products: 1000,
  shipments: 1000
}

export default {
  name: 'PriceManager',
  inject: ['$validator'],
  data () {
    return {
      maxInputLegend: { ...inputValuesLegend },
      showLoader: false,
      // v-models
      discountType: 'percentage',
      discountValue: '',
      levelType: 'products',
      levelTypeId: ''
    }
  },
  computed: {
    getShippingAmount () {
      return this.$store.getters['cart/getShippingAmount']
    },
    cartItemsTotal () {
      return this.$store.getters['cart/cartItemsTotal']
    },
    totalAmount () {
      return this.$store.getters['cart/totalAmount']
    },
    totalAmountWithCurrency () {
      return this.$options.filters.price(this.totalAmount, this.$store)
    },
    canAddMore () {
      // return message if total is 0 and cart has price adjustments
      return this.totalAmount > 0
    },
    maxPriceForInput () {
      const inputValidator = {
        amount: {
          products: this.maxInputLegend['products'],
          shipments: this.maxInputLegend['shipments'],
          order: this.totalAmount
        },
        percentage: {
          products: this.maxInputLegend['products'] === 0 ? 0 : 100,
          shipments: this.maxInputLegend['shipments'] === 0 ? 0 : 100,
          order: this.totalAmount === 0 ? 0 : 100
        }
      }
      return inputValidator[this.discountType][this.levelType]
    }
  },
  methods: {
    setActiveTile (activeTile) {
      // get amount from active tile
      // with amout set max value for price input
      if (activeTile && (this.levelType in this.maxInputLegend)) {
        this.maxInputLegend[this.levelType] = activeTile.final_price
      } else {
        this.maxInputLegend = { ...inputValuesLegend }
      }
      return activeTile
    },
    resetModels () {
      this.$validator.reset()
      this.discountValue = ''
      // reset if different from order
      // order has no selection and for validation reason is setted to fake value
      // check this component for more info /theme/components/Checkout/PriceAdjustements/Tabs/OrderTab.vue
      if (this.levelType !== 'order') {
        this.levelTypeId = ''
      }
    },
    prepareForAdd () {
      // prepare payload for add required by getDataByIdentifier
      return {
        identifier: this.levelType,
        price: {
          promotion_id: `appSalesPromotionsOrder${randomInRange(100000000, 1000000000)}`,
          item_id: this.levelTypeId,
          applied_discount: {
            amount: parseFloat(this.discountValue),
            type: this.discountType
          }
        }
      }
    },
    // salsforce has problem while adding adjusments
    // mixin percentage and amount result of produxt price may goes to below 0
    // preventing this error
    // check if product, shipment or order has at least of discount type percentage and amount
    // if yes prevent adding percentage.
    checkForTypeAvailability () {
      if (this.discountType === 'percentage') {
        let prices = this.$store.getters['cart/getPricesAdjustments']
        if (prices[this.levelType] && prices[this.levelType].length) {
          return !prices[this.levelType].find((price) => {
            return price.item_id === this.levelTypeId && price.applied_discount.type === 'percentage'
          })
        }
      }
      return true
    },
    addPrice () {
      if (this.canAddMore) {
        if (!this.checkForTypeAvailability()) {
          return toast.error(this.$t('price-adj.messages.cannot-add-by-type-percentage'))
        }
        return this.$validator.validateAll()
          .then(isValid => {
            if (isValid) {
              let payload = this.prepareForAdd()
              this.makeCall(payload)
            }
          })
      }
    },
    removePrice (payload) {
      this.makeCall(payload, true)
    },
    makeCall (payload, isRemove = false) {
      this.showLoader = true
      let remove = isRemove ? 'REMOVE' : ''
      return this.$store.dispatch('checkout/managePriceAdjustments', getDataByIdentifier(payload, remove))
        .then(() => {
          this.resetModels()
          return this.$store.dispatch('cart/totals')
        })
        .catch((err) => {
          if (err.message) {
            toast.error(this.$t(err.message))
          } else {
            toast.error(this.$t('GenericError'))
          }
        })
        .finally(() => {
          this.showLoader = false
        })
    }
  },
  components: {
    AbsoluteLoader,
    PriceInput,
    TabsManager,
    ActivePrices
  }
}
</script>
